import * as R from 'ramda';
import React, { memo } from 'react';
// components
import { TextComponent } from '../../../../components/text';
// helpers/constants
import * as G from '../../../../helpers';
import * as GC from '../../../../constants';
// icons
import * as I from '../../../../svgs';
// ui
import { Box, Flex, IconWrapper, SectionsDivider, MainActionButton } from '../../../../ui';
//////////////////////////////////////////////////

const darkGreyColor = G.getTheme('colors.#7D828C');
const lightGreyColor = G.getTheme('colors.lightGrey');

const RowActions = ({ handleEditEstimate, handleRemoveEstimate }) => (
  <Flex width={35} justifyContent='space-between'>
    <IconWrapper cursor='pointer' onClick={handleEditEstimate}>
      {I.pencil(darkGreyColor)}
    </IconWrapper>
    <IconWrapper cursor='pointer' onClick={handleRemoveEstimate}>
      {I.trash(darkGreyColor)}
    </IconWrapper>
  </Flex>
);

const InvoiceInfoPair = ({ text, maxWidth }) => (
  <TextComponent
    mr={10}
    title={text}
    display='block'
    withEllipsis={true}
    color={darkGreyColor}
    maxWidth={R.or(maxWidth, 170)}
  >
    {text}
  </TextComponent>
);

const PrimaryLocation = ({ primaryLocation }) => {
  const location = G.concatLocationFields(primaryLocation);
  const contact = G.concatContactFields(primaryLocation, [GC.FIELD_CONTACT_NAME, GC.FIELD_PHONE, GC.FIELD_EMAILS]);

  const info = G.createStringFromArray([location, contact], ', ');

  const label = G.getWindowLocale('titles:primary-location', 'Primary Location');

  const text = `${label}: ${info}`;

  return (
    <TextComponent
      title={text}
      display='block'
      maxWidth='100%'
      withEllipsis={true}
      color={darkGreyColor}
    >
      {text}
    </TextComponent>
  );
};

const PayToLocation = ({ payToLocation }) => {
  const location = G.concatLocationFields(payToLocation);

  const label = G.getWindowLocale('titles:pay-to', 'Pay To');

  const text = `${label}: ${location}`;

  return (
    <TextComponent
      title={text}
      display='block'
      maxWidth='100%'
      withEllipsis={true}
      color={darkGreyColor}
    >
      {text}
    </TextComponent>
  );
};

const VendorInfo = (props) => {
  const { estimate, serviceVendorListAvailableOptions } = props;

  const { serviceVendorGuid } = estimate;

  const serviceVendor = R.find(R.propEq(serviceVendorGuid, GC.FIELD_VALUE), serviceVendorListAvailableOptions);

  if (G.isNilOrEmpty(serviceVendor)) return null;

  const { dba, label, dunsNumber, payToLocation, primaryLocation } = serviceVendor;

  const info = G.createStringFromArray([label, dba, dunsNumber], ', ');

  const text = `${G.getWindowLocale('titles:vendor', 'Vendor')}: ${info}`;

  return (
    <Box m={15}>
      <TextComponent
        title={text}
        display='block'
        maxWidth='100%'
        withEllipsis={true}
        color={darkGreyColor}
      >
        {text}
      </TextComponent>
      {
        G.isNotNilAndNotEmpty(primaryLocation) &&
        <PrimaryLocation primaryLocation={primaryLocation} />
      }
      {
        G.isNotNilAndNotEmpty(payToLocation) &&
        <PayToLocation payToLocation={payToLocation} />
      }
    </Box>
  );
};

const TotalRow = ({ costs, titleLocaleArr, currencySymbol }) => {
  const total = G.getCostsSubTotal(costs);

  const title = `${currencySymbol} ${total}`;

  return (
    <Flex my='5px' color={darkGreyColor}>
      <Box mr='5px' width={60}>{G.getWindowLocale(...titleLocaleArr)}:</Box>
      <TextComponent
        title={title}
        maxWidth={300}
        display='block'
        withEllipsis={true}
      >
        {title}
      </TextComponent>
    </Flex>
  );
};

const EstimateInfo = memo((props) => {
  const {
    estimate,
    createInvoice,
    handleRemoveEstimate,
    handleAddOrEditEstimate,
    serviceVendorListAvailableOptions,
  } = props;

  const {
    guid,
    date,
    fees,
    parts,
    labors,
    number,
    currency,
  } = estimate;

  const currencySymbol = G.getCurrencySymbol(currency);

  const costsTotal = G.getCostsTotal(estimate);

  const invoiceNumberText = `#: ${number}`;
  const invoiceDateText = `${G.getWindowLocale('titles:date', 'Date')}: ${date}`;
  const invoiceTotalText = `${G.getWindowLocale('titles:total', 'Total')}: ${currencySymbol} ${costsTotal}`;

  return (
    <Box
      mb={10}
      width='100%'
      flexShrink={0}
      borderRadius='6px'
      border='1px solid'
      borderColor={darkGreyColor}
      background={G.getTheme('colors.white')}
    >
      <Flex
        px={15}
        height={32}
        width='100%'
        color={darkGreyColor}
        borderTopLeftRadius='6px'
        borderTopRightRadius='6px'
        justifyContent='space-between'
        bg={G.getTheme('colors.borderGray')}
      >
        <Flex>
          <Box mr='5px'>
            {G.getWindowLocale('titles:estimate', 'Estimate')}
          </Box>
          {G.isNotNilAndNotEmpty(number) && <InvoiceInfoPair text={invoiceNumberText} />}
          {G.isNotNilAndNotEmpty(date) && <InvoiceInfoPair maxWidth={120} text={invoiceDateText} />}
        </Flex>
        <RowActions
          handleRemoveEstimate={() => handleRemoveEstimate(guid)}
          handleEditEstimate={() => handleAddOrEditEstimate(estimate)}
        />
      </Flex>
      <VendorInfo estimate={estimate} serviceVendorListAvailableOptions={serviceVendorListAvailableOptions} />
      <Box px={15}>
        <TotalRow
          costs={labors}
          currencySymbol={currencySymbol}
          titleLocaleArr={['titles:labor', 'Labor']}
        />
        <TotalRow
          costs={parts}
          currencySymbol={currencySymbol}
          titleLocaleArr={['titles:parts', 'Parts']}
        />
        <TotalRow
          costs={fees}
          currencySymbol={currencySymbol}
          titleLocaleArr={['titles:fees', 'Fees']}
        />
      </Box>
      <SectionsDivider borderColor={lightGreyColor} />
      <Flex p='8px 15px' justifyContent='space-between'>
        <Box
          fontWeight={700}
          textTransform='uppercase'
          color={G.getTheme('colors.light.blue')}
        >
          {invoiceTotalText}
        </Box>
        <MainActionButton
          height={22}
          fontSize={11}
          type='button'
          onClick={() => createInvoice(estimate)}
        >
          {G.getWindowLocale('titles:create-invoice', 'Create Invoice')}
        </MainActionButton>
      </Flex>
    </Box>
  );
});

export default EstimateInfo;
